import React, { useState, useEffect } from 'react';
import { CustomInput } from '../components/ui/CustomInput';
import { CustomButton } from '../components/ui/CustomButton';
import { useLogin, useGoogleLogin, useGoogleLoginCallback } from '../hooks/useAuth';

interface LoginScreenProps {
  onLoginSuccess: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const loginMutation = useLogin();
  const googleLoginMutation = useGoogleLogin();
  const googleCallbackMutation = useGoogleLoginCallback();

  // Handle Google OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      // Clear the URL parameters to avoid repeated processing
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Complete Google login with the authorization code
      const redirectUri = 'https://dev-uc.taliaz-cm.com/login';
      googleCallbackMutation.mutate(
        { code, redirectUri },
        {
          onSuccess: () => {
            onLoginSuccess();
          },
          onError: (err: any) => {
            setError(err.message || 'Google login failed. Please try again.');
          }
        }
      );
    }
  }, [googleCallbackMutation, onLoginSuccess]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!username || !password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');
    
    try {
      await loginMutation.mutateAsync({ username, password });
      onLoginSuccess();
    } catch (err: any) {
      setError(err.message || 'Invalid credentials. Please try again.');
    }
  };

  const handleButtonClick = () => {
    handleSubmit();
  };

  const handleGoogleLogin = () => {
    
    setError('');
    const redirectUri = 'https://dev-uc.taliaz-cm.com/login';
    googleLoginMutation.mutate(redirectUri);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header Image Area */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-primaryBg to-white px-4">
        <div className="w-full max-w-md">
          <div className="bg-darkGreen rounded-full w-32 h-32 mx-auto mb-8 flex items-center justify-center">
            <svg 
              className="w-16 h-16 text-white" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2M21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H11V21H5V3H13V9H21Z"/>
              <path d="M17 13C17.55 13 18 13.45 18 14S17.55 15 17 15 16 14.55 16 14 16.45 13 17 13M17 11C15.34 11 14 12.34 14 14S15.34 17 17 17 20 15.66 20 14 18.66 11 17 11Z"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-darkGreen text-center mb-2">
            Urgent Consulting
          </h1>
          <p className="text-grayText text-center mb-8">
            Healthcare consultation management
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="bg-primaryBg rounded-t-3xl px-6 py-8 min-h-[50vh]">
        <div className="max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-black mb-6">
            Login
          </h2>
          
          {(error || googleLoginMutation.error || googleCallbackMutation.error) && (
            <div className="flex items-center gap-2 p-4 border border-borderRed rounded-xl bg-redBg mb-4">
              <svg className="w-5 h-5 text-redText" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="text-redText text-sm">
                {error || googleLoginMutation.error?.message || googleCallbackMutation.error?.message || 'Authentication failed. Please try again.'}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <CustomInput
              placeholder="Username or ID"
              value={username}
              onChange={setUsername}
              error={!!error && !username}
            />
            
            <CustomInput
              placeholder="Password"
              value={password}
              onChange={setPassword}
              type="password"
              error={!!error && !password}
            />


            <CustomButton
              title="Login"
              onClick={handleButtonClick}
              loading={loginMutation.isPending}
              disabled={loginMutation.isPending}
              className="w-full"
            />
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>

          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={googleLoginMutation.isPending}
            className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-xl py-3 px-4 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {googleLoginMutation.isPending ? (
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            <span className="text-gray-700 font-medium">
              {googleLoginMutation.isPending ? 'Connecting...' : 'Continue with Google'}
            </span>
          </button>

          <div className="flex items-center justify-center gap-2 mt-6">
            <span className="text-black text-sm">
              Need help?
            </span>
            <button
              type="button"
              className="text-darkGreen text-sm font-medium hover:underline"
              onClick={() => console.log('Contact support')}
            >
              Contact Support
            </button>
            <svg className="w-4 h-4 text-darkGreen" fill="currentColor" viewBox="0 0 24 24">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};